import { useQuery } from '@tanstack/react-query'
import { clockTime, meridiem } from '../api/attendance'
import { fetchCoverEvening, timeRange, type CoverEvening, type CoverEveningRow } from '../api/cover'
import Modal from './Modal'

/**
 * A night she is being offered, with her own evening around it (D-776).
 *
 * **The gap it closes.** The board named a class and the hour it started, and
 * that was the whole of what a teacher had to decide on. Hers, from the pilot:
 * *"I don't know how long the class is. I teach my own class at 7, and I know
 * her class is 6:15... till?"* The end time is on the row now; this is the
 * other half — what she already has either side of it.
 *
 * **Read-only, deliberately.** The obvious reach was to extend the room-booking
 * flow, which is where the studio's other "when am I free" screens live. But
 * she is not booking anything: a room picker, a duration and a dancer list are
 * all the wrong questions, and bending that form would mean hiding most of it.
 * What she needs is her day, with this class laid into it.
 *
 * **Taking it is still the board's claim**, passed in rather than rebuilt here,
 * so there is one path and one set of refusals (A-17). This screen decides
 * nothing about who may cover.
 */
export default function CoverEveningSheet({
  absenceId,
  onDismiss,
  onTake,
  taking,
  error,
}: {
  absenceId: string
  onDismiss: () => void
  onTake: () => void
  taking: boolean
  /** What the server said when a claim was refused, so it lands where she tapped. */
  error: string | null
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['cover', 'evening', absenceId],
    queryFn: () => fetchCoverEvening(absenceId),
  })

  return (
    <Modal onDismiss={onDismiss} labelledBy="cover-evening-title" panelClassName={PANEL}>
      <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-2 px-4 py-3">
        <h2 id="cover-evening-title" className="font-display text-[15px] text-ink">
          {data ? dayName(data.date) : 'This class'}
        </h2>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="min-h-[44px] min-w-[44px] text-[19px] leading-none text-ink-3 hover:text-ink"
        >
          &times;
        </button>
      </div>

      <div className="px-4 py-3.5">
        {/*
          **Pending and failed are different sentences** (D-209). A sheet that
          said "nothing else on" while the lookup was refused would be a
          statement about her evening made out of not knowing — and she would
          take the class on it.
        */}
        {isPending && <p className="py-6 text-center text-[12.5px] text-ink-3">Looking at your day&hellip;</p>}

        {isError && (
          <p className="py-6 text-center text-[12.5px] text-ink-3">
            Your day could not be loaded, so this is not a picture of it. Try again in a moment.
          </p>
        )}

        {data && (
          <>
            <div className="mb-3.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5">
              <span className="block text-[12.5px] leading-snug font-semibold wrap-anywhere text-ink-2">
                {data.className}
              </span>
              {/*
                **One string, and the start is checked before it is formatted.**
                `timeRange('')` prints a leading dash — a class with no start
                time meets on no dates (D-84), which is a thing to say rather
                than a half-rendered range. The board's own `coverWhen` has
                always guarded this; this line did not, which an audit caught.
              */}
              <span className="mt-0.5 block text-[12px] text-ink-3">{about(data)}</span>
              <span className="mt-0.5 block text-[12px] text-ink-3">{data.absentName} is out</span>

              {data.note && (
                <span className="mt-1 block text-[12px] wrap-anywhere text-ink-3 italic">
                  &ldquo;{data.note}&rdquo;
                </span>
              )}
            </div>

            <Verdict evening={data.evening} clashes={data.clashes} endKnown={data.endTime !== null} />

            <h3 className="mb-2 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
              Your evening
            </h3>

            {/*
              Nothing else on is a real answer and the most reassuring one, so
              it is said rather than left as an empty box.

              **Counted without the offered row, not as "one row"** — the server
              leaves the offered class out entirely when it has no start time
              (D-84), so a length of one there is her own commitment and this
              would have said she had nothing while hiding the one thing she
              had. Found by an audit.
            */}
            {data.evening.filter((r) => !r.isOffered).length === 0 ? (
              <p className="text-[12.5px] text-ink-3">
                You have nothing else at the studio that day.
              </p>
            ) : (
              <ol className="flex flex-col">
                {data.evening.map((row, i) => (
                  <Row key={`${row.start}-${row.label}-${i}`} row={row} />
                ))}
              </ol>
            )}

            {error && <p className="mt-3 text-[12px] text-crit">{error}</p>}

            <div className="mt-4 flex flex-wrap gap-2.5">
              {/*
                **The clash is a sentence and never a disabled button** — the
                board makes the same choice, for the same reason: a control that
                refuses without saying why reads as the app being broken. What
                this adds is which class is in the way, which is what tells her
                whether it is worth asking the studio to move something.
              */}
              {data.clashes ? (
                <p className="flex-1 rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-[12.5px] leading-snug text-ink-2">
                  <strong className="font-semibold">You are busy then.</strong>{' '}
                  {overlapping(data.evening)
                    ? `${overlapping(data.evening)} runs into it.`
                    : 'Something else of yours runs into it.'}{' '}
                  The studio can move things about; this board cannot.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={onTake}
                  disabled={taking}
                  aria-label={`Take ${data.className}`}
                  className="min-h-[44px] flex-1 rounded-lg border border-accent bg-accent-soft px-3 py-2.5 text-[12.5px] font-semibold text-accent-2 hover:border-ink-3 disabled:opacity-60"
                >
                  {taking ? 'Taking...' : 'Take it'}
                </button>
              )}

              <button
                type="button"
                onClick={onDismiss}
                className="min-h-[44px] rounded-lg border border-line-2 px-3 py-2.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
              >
                {data.clashes ? 'Close' : 'Not this time'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

const PANEL =
  'w-full max-w-[400px] overflow-hidden rounded-xl border border-line bg-surface shadow-lg'

/**
 * The answer, before the detail.
 *
 * She opened this to decide, so the decision goes at the top and the timeline
 * below it is the working. A sheet that made her read four rows to reach the
 * same conclusion would be the arithmetic it exists to remove.
 */
function Verdict({
  evening,
  clashes,
  endKnown,
}: {
  evening: CoverEveningRow[]
  clashes: boolean
  endKnown: boolean
}) {
  const tone = clashes
    ? 'border-crit bg-crit-bg text-crit'
    : 'border-line bg-surface-2 text-ink-2'

  const hit = overlapping(evening)

  return (
    <p className={`mb-3.5 rounded-lg border px-3 py-2.5 text-[12.5px] leading-snug ${tone}`}>
      {clashes ? (
        <>
          <strong className="block font-semibold">This runs into something of yours.</strong>
          {hit ? `${hit} is in the way.` : 'Another of your sessions is in the way.'}
        </>
      ) : (
        <>
          <strong className="block font-semibold">Nothing of yours is in the way.</strong>
          {/*
            **Said out loud when the class has no end time**, because "nothing
            in the way" is a weaker claim than it looks: the overlap was tested
            against an assumed hour. She is the one who can find out.
          */}
          {endKnown
            ? 'Your evening has room for it.'
            : 'The studio has not recorded when this class finishes, so that was checked against an hour.'}
        </>
      )}
    </p>
  )
}

/** The first of hers that runs into it, for the sentence above the list. */
function overlapping(evening: CoverEveningRow[]): string | null {
  return evening.find((r) => r.overlaps)?.label ?? null
}

function Row({ row }: { row: CoverEveningRow }) {
  const offered = row.isOffered

  const card = offered
    ? 'border-accent bg-accent-soft text-accent-2'
    : row.overlaps
      ? 'border-crit bg-crit-bg text-crit'
      : 'border-line bg-surface-2 text-ink-2'

  return (
    <li className="grid grid-cols-[52px_1fr] gap-2.5">
      <span className="pt-2 text-right text-[11.5px] tabular-nums text-ink-3">
        {clockTime(row.start)}
        {meridiem(row.start)}
      </span>

      <span className="border-l-2 border-line py-1.5 pl-3">
        <span className={`block rounded-lg border px-2.5 py-1.5 ${card}`}>
          <span className="block text-[12.5px] leading-snug font-semibold wrap-anywhere">
            {row.label}
          </span>
          {/*
            **One string rather than three nodes.** Split across
            `{a}{' · '}{b}` it renders identically and cannot be matched as a
            sentence by anything reading the text - which a test found first.
          */}
          <span className="mt-0.5 block text-[11.5px] opacity-80">{meta(row)}</span>
        </span>
      </span>
    </li>
  )
}

/**
 * *6:15&ndash;7:15pm &middot; Studio B &middot; 9 dancers*, with whatever is
 * missing left out rather than rendered as an empty gap.
 *
 * The dancer count is who is **enrolled**; a register can also carry a makeup
 * visitor (D-398), so it is the size of the class rather than a promise about
 * who will be in the room.
 */
function about(data: CoverEvening): string {
  const when = data.startTime
    ? timeRange(data.startTime, data.endTime)
    : 'No start time recorded'

  const dancers = data.enrolled > 0 ? `${data.enrolled} enrolled` : null

  return [when, data.roomName, dancers].filter(Boolean).join(' · ')
}

/**
 * *Yours &middot; until 8:00pm*, or *Offered to you &middot; no finish time recorded*.
 *
 * **"until" only when the studio said so.** A class with no end time is an hour
 * long to the overlap test, because it has to compare something. Printing that
 * hour here would answer the question she opened this sheet to ask with a time
 * nobody set.
 */
function meta(row: CoverEveningRow): string {
  const kind = row.isRoomBooking ? 'Room you booked' : row.isOffered ? 'Offered to you' : 'Yours'

  const ends = row.endKnown
    ? `until ${clockTime(row.end)}${meridiem(row.end)}`
    : 'no finish time recorded'

  return `${kind} · ${ends}`
}

/** *Tuesday 29 September*, as the heading for a day she is deciding about. */
function dayName(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}
